/**
 * The admin app's reads.
 *
 * Two of these run against rules the Studio never meets, and both are worth stating:
 *
 * - **The whole catalog, published or not.** `mediums` reads are open for a published or
 *   coming-soon row and admin-only for the rest, so an unfiltered listing is exactly the
 *   query an admin is *for*. Ordering is the admin's own `order` field.
 * - **A global render is fetchable by id and never listed.** `variantInstances` gates `list`
 *   on `requestedBy`, and a global render deliberately carries none — that is what stops one
 *   author enumerating another's cache. So the matrix here reads **one document per cell**
 *   rather than one query per primitive, which is the honest cost of a record that belongs to
 *   nobody. It is a handful of listeners on an internal tool; a batched read would need a
 *   field that re-introduces the thing the rule removes.
 */

import type { Primitive, VariantInstance } from '@atlas-nook/story';
import type { Medium } from '@atlas-nook/mediums';
import { orderBy, query, where } from 'firebase/firestore';

import {
  globalPrimitive,
  globalPrimitives,
  issue,
  issues,
  medium,
  mediums,
  variantInstance,
} from './collections.js';
import type { Issue } from './issues.js';
import { useDocument, useQueryDocs } from './subscribe.js';
import type { Subscription } from './subscribe.js';

/** The whole catalog, in the admin's order. Unpublished rows included — that is the point. */
export function useCatalog(): Subscription<Medium[]> {
  const subscription = useQueryDocs(query(mediums(), orderBy('order')), 'mediums:all');
  return subscription;
}

export function useMedium(id: string | undefined): Subscription<Medium | null> {
  return useDocument(id === undefined ? null : medium(id), `medium:${id ?? ''}`);
}

/**
 * The curated global library, archived rows included.
 *
 * Archived globals vanish from Browse (the Studio filters `archivedAt == null`) but keep
 * resolving, so the admin has to be able to see and restore them — this is the one listing
 * in the product that shows both.
 */
export function useGlobals(): Subscription<Primitive[]> {
  return useQueryDocs(query(globalPrimitives(), where('scope', '==', 'global')), 'primitives:all');
}

export function useGlobal(id: string | undefined): Subscription<Primitive | null> {
  return useDocument(id === undefined ? null : globalPrimitive(id), `primitive:${id ?? ''}`);
}

/** One render by content address. A miss is a fact: the address has not been drawn. */
export function useVariantInstance(id: string | undefined): Subscription<VariantInstance | null> {
  return useDocument(id === undefined ? null : variantInstance(id), `variantInstance:${id ?? ''}`);
}

/**
 * The issues tracker, newest first.
 *
 * `orderBy('createdAt')` is doing two jobs. The obvious one is the order — a tracker read by
 * a person is read from the top. The quiet one is that a Firestore query **skips a document
 * that does not carry the ordered field**, and `issues/_meta` — the id counter, which shares
 * this collection — carries no `createdAt`. So the counter is excluded by the same clause
 * that sorts, with no `!=` filter and no composite index. `normalizeIssue` refuses it too,
 * belt and braces.
 *
 * Status and area are filtered **in the surface**, not in the query: they are two orthogonal
 * multi-selects over a list that is measured in dozens, and pushing them into Firestore would
 * buy a composite index per combination and a lot of re-subscribing for nothing.
 */
export function useIssues(): Subscription<Issue[]> {
  return useQueryDocs(query(issues(), orderBy('createdAt', 'desc')), 'issues:all');
}

export function useIssue(id: string | undefined): Subscription<Issue | null> {
  return useDocument(id === undefined ? null : issue(id), `issue:${id ?? ''}`);
}
